import React, { useEffect } from 'react';
import { DataLayer } from '../../../lib/types';

/**
 * Inline spinner + uniform "Loading {layer.displayName}…" copy. Single
 * source of truth so panels can't drift on tone (we previously had
 * "Loading AGB time series…" / "Loading per-tree attributes…" /
 * "Loading imperiled species…" — three voices for the same operation).
 */
export const LayerLoadingLabel: React.FC<{ layer: DataLayer }> = ({ layer }) => (
  <div className="flex items-center gap-2 text-figma-lime text-body font-roboto">
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
    Loading {layer.displayName}&hellip;
  </div>
);

/**
 * Yellow alert card for data-fetch failures. The body copy is intentionally
 * generic — we never surface raw error strings (`fetch failed: 404`,
 * `JSON.parse SyntaxError`) to users. The actual fetch error is logged
 * to the console via the optional `details` prop, which keeps prod
 * traces useful for debugging without leaking internals to the panel.
 */
export const LayerErrorAlert: React.FC<{ layer: DataLayer; details?: string }> = ({
  layer,
  details,
}) => {
  useEffect(() => {
    if (details) {
      console.warn(`[${layer.id}] data load failed:`, details);
    }
  }, [layer.id, details]);

  return (
    <div className="bg-figma-card rounded-figma-panel p-5 border border-yellow-500/30">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="space-y-1">
          <p className="text-sm font-roboto font-semibold text-yellow-400">
            {layer.displayName} unavailable
          </p>
          <p className="text-xs font-roboto text-neutral-400">
            Couldn&rsquo;t load this data. Try refreshing the page.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * "Live data from CTrees S3" badge shown above each layer's live chart.
 */
export const LayerLiveBadge: React.FC = () => (
  <div className="text-xs font-roboto-mono text-green-400/70">
    Live data from CTrees S3
  </div>
);

/**
 * Single pulsing block — used in loading skeletons as a chart stand-in.
 * Caller passes a Tailwind height class (e.g. `h-[260px]`).
 */
export const SkeletonPulseBlock: React.FC<{ className: string }> = ({ className }) => (
  <div className={`bg-figma-surface rounded-figma-panel animate-pulse ${className}`} />
);

/**
 * 4-card pulsing grid — matches the stat-card grid that most layers show
 * below their chart. `count` defaults to 4 since every existing caller
 * uses that.
 */
export const SkeletonStatCardGrid: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-3">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
        <div className="h-3 w-20 bg-figma-surface rounded animate-pulse mb-2" />
        <div className="h-6 w-24 bg-figma-surface rounded animate-pulse" />
      </div>
    ))}
  </div>
);
